import { Box, Popover, type PopoverOrigin } from '@mui/material';
import { colors } from '@sweep-io/sweep-design';
import {
  colorPickerLightColors,
  colorPickerDarkColors,
  getColorPickerColorLabel,
} from './colorPickerColors';
import { ColorSwatch } from './ColorSwatch';

const LOG_COLORS = false;

const getLightColorList = () => [
  colorPickerLightColors.White,
  colorPickerLightColors.Grey150,
  colorPickerLightColors.Seafoam,
  colorPickerLightColors.Blush,
  colorPickerLightColors.Sun,
  colorPickerLightColors.Lemon,
  colorPickerLightColors.Grey300,
  colorPickerLightColors.Sky100,
  colorPickerLightColors.Blue,
  colorPickerLightColors.Lilac,
  colorPickerLightColors.Gum,
  colorPickerLightColors.Mint,
];

const getDarkColorList = () => [
  colorPickerDarkColors.Black,
  colorPickerDarkColors.White,
  colorPickerDarkColors.Grey700,
  colorPickerDarkColors.Grey500,
  colorPickerDarkColors.Seafoam300,
  colorPickerDarkColors.Blush500,
  colorPickerDarkColors.Sun500,
  colorPickerDarkColors.Lemon500,
  colorPickerDarkColors.Sky500,
  colorPickerDarkColors.Grey300,
  colorPickerDarkColors.Blue500,
  colorPickerDarkColors.Gum500,
];

type Position = 'right' | 'top';

const getPositioning = (
  position: Position,
): { anchorOrigin: PopoverOrigin; transformOrigin: PopoverOrigin } => {
  switch (position) {
    case 'right':
      return {
        anchorOrigin: { vertical: 'center', horizontal: 'right' },
        transformOrigin: { vertical: 'center', horizontal: 'left' },
      };
    case 'top':
      return {
        anchorOrigin: { vertical: -12, horizontal: 'center' },
        transformOrigin: { vertical: 'bottom', horizontal: 'center' },
      };
    default:
      return {
        anchorOrigin: { vertical: 'center', horizontal: 'right' },
        transformOrigin: { vertical: 'center', horizontal: 'left' },
      };
  }
};

export const ColorPicker = ({
  anchorElement,
  closePopover,
  onColorChange,
  selectedColor = colors.white,
  singleRow: singleRow,
  position = 'right',
  palette = 'light',
}: {
  anchorElement: EventTarget & Element;
  closePopover: () => void;
  onColorChange: (color: string) => void;
  selectedColor?: string;
  singleRow?: boolean;
  position?: Position;
  palette?: 'light' | 'dark';
}) => {
  const { anchorOrigin, transformOrigin } = getPositioning(position);

  const colorList = palette === 'light' ? getLightColorList() : getDarkColorList();
  const colorList1 = colorList.slice(0, 6);
  const colorList2 = colorList.slice(6);

  const _onColorChange = (color: string) => {
    onColorChange(color);
    if (LOG_COLORS) {
      const label = getColorPickerColorLabel(color, palette);
      console.log('ColorPicker', label);
    }
  };

  return (
    <Popover
      open
      anchorEl={anchorElement}
      onClose={closePopover}
      slotProps={{
        paper: {
          sx: {
            padding: 1,
            display: 'flex',
            flexDirection: singleRow ? 'row' : 'column',
            gap: 1,
          },
        },
      }}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
    >
      {singleRow ? (
        <Box display="flex" gap={1}>
          {colorList.map((color) => (
            <ColorSwatch
              key={color}
              color={color}
              selected={color === selectedColor}
              onClick={() => _onColorChange(color)}
            />
          ))}
        </Box>
      ) : (
        <>
          <Box display="flex" gap={1}>
            {colorList1.map((color) => (
              <ColorSwatch
                key={color}
                color={color}
                selected={color === selectedColor}
                onClick={() => _onColorChange(color)}
              />
            ))}
          </Box>
          <Box display="flex" gap={1}>
            {colorList2.map((color) => (
              <ColorSwatch
                key={color}
                color={color}
                selected={color === selectedColor}
                onClick={() => _onColorChange(color)}
              />
            ))}
          </Box>
        </>
      )}
    </Popover>
  );
};
